// NOTE: The narrative copy on this page is a DRAFT written to the
// "free, generous, creator-first" positioning found elsewhere in the app.
// No real founding story, date, or team details exist in the codebase —
// this needs sign-off from the Linkakam team before it ships as final copy.
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "react-i18next";
import Button from "../componant/ui/Button";

gsap.registerPlugin(ScrollTrigger);

const VALUES = [
  {
    title: "Free means free",
    body: "Your link page, forms and analytics stay free as your audience grows. No feature paywall waiting for you at 1,000 followers.",
  },
  {
    title: "Built for creators first",
    body: "Every tool we ship — link pages, QR codes, CVs, storefronts — starts from what people actually build with their bio link, not what upsells well.",
  },
  {
    title: "One link, fully yours",
    body: "Your profile, your links, your data. Export it, move it, or take it down whenever you want.",
  },
];

export default function About() {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-reveal]").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 20,
          duration: 0.7,
          ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 85%" },
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={containerRef} className="bg-white">
      <section className="max-w-8xl mx-auto px-6 md:px-10 pt-20 pb-16">
        <p data-reveal className="text-sm font-semibold uppercase tracking-wide text-brand-600">
          {t("About Linkakam")}
        </p>
        <h1
          data-reveal
          className="mt-4 font-display font-extrabold text-3xl md:text-4xl leading-tight text-ink-900"
        >
          {t("A link in bio tool that doesn't paywall the basics.")}
        </h1>
        <p data-reveal className="mt-6 text-lg text-ink-500 leading-relaxed">
          {t(
            "Linkakam started from a simple frustration: the tools creators need most — a clean link page, a working contact form, real analytics — are usually the first things locked behind a subscription. We built Linkakam the other way around."
          )}
        </p>
      </section>

      <section className="max-w-8xl mx-auto px-6 md:px-10 py-4">
        <div data-reveal className="border-l-2 border-accent-500 pl-6 py-2">
          <p className="text-xl md:text-2xl font-display font-semibold text-ink-800 leading-snug">
            {t(
              "“We'd rather earn trust by being useful for free than earn revenue by holding features back.”"
            )}
          </p>
        </div>
      </section>

      <section className="max-w-8xl mx-auto px-6 md:px-10 py-20">
        <div className="grid gap-12 md:grid-cols-3">
          {VALUES.map((v) => (
            <div data-reveal key={v.title}>
              <h2 className="font-display font-bold text-lg text-ink-900">
                {t(v.title)}
              </h2>
              <p className="mt-3 text-sm text-ink-500 leading-relaxed">
                {t(v.body)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink-50">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center">
          <h2
            data-reveal
            className="font-display font-extrabold text-2xl md:text-3xl text-ink-900"
          >
            {t("Build your page in a few minutes.")}
          </h2>
          <p data-reveal className="mt-4 text-ink-500">
            {t("No credit card. No trial countdown. Just your links, live.")}
          </p>
          <div data-reveal className="mt-8">
            <Button to="/en/setup" variant="primary" size="lg">
              {t("Create your link")}
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
